import React from 'react';

import { useQueryClient } from '@tanstack/react-query';
import useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges from 'components/hooks/useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { openCreateOKRPanel } from 'reducers/createObjectivePanelSlice';
import { okrsPageSelector } from 'selectors/okrsPage';

import { Plus } from 'assets/assetsV1/iconComponents';
import GoalsNoDataImageV1 from 'assets/assetsV1/illustrations/newIllustrations/noGoal.svg';
import GoalsNoDataImage2 from 'assets/assetsV1/illustrations/newIllustrations/noGoals2.svg';

import { OKR_TYPES } from 'constants/ProjectConstants';
import { modifyString, StringModifier } from 'constants/copyHelpers';
import { isMobileView } from 'constants/isMobileView';
import { sendCreatedKREvent, sendCreatedObjectiveEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { Button } from 'uiToolkitV1/Button';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';

const CTA = () => {
  const location = useLocation();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { canCreateKeyResultUnderKeyResult, canCreateKeyResultUnderObjective, canCreateKeyResultAtRootLevel } =
    useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges();

  const canCreateKeyResult =
    canCreateKeyResultAtRootLevel || canCreateKeyResultUnderObjective || canCreateKeyResultUnderKeyResult;

  const refetchCascadedViewData = () => {
    queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsCascadedData]);
  };

  const { selectedTimelines } = useSelector(okrsPageSelector);

  const dispatch = useDispatch();

  const onAddKR = () => {
    dispatch(
      openCreateOKRPanel({
        selectedObjective: null,
        selectedTimeBoundPeriod: selectedTimelines?.[0],
        okrPanelType: OKR_TYPES.KEY_RESULT,
        onKeyResultCreation(okrDetails) {
          sendCreatedKREvent({
            eventProperties: {
              source: 'Add New Button',
              goalId: okrDetails?.uuid,
            },
          });
          refetchCascadedViewData();
        },
        onObjectiveCreation(okrDetails) {
          sendCreatedObjectiveEvent({
            eventProperties: {
              source: 'Add New Button',
              objectiveId: okrDetails?.uuid,
              description: okrDetails?.description,
              level: okrDetails?.objectiveLevel,
              type: okrDetails?.type,
              timePeriod: okrDetails?.timeBoundDetails?.timelineTag,
            },
          });
          refetchCascadedViewData();
        },
        history,
        location,
      })
    );
  };

  return canCreateKeyResult ? (
    <Button style={{ pointerEvents: 'auto', marginBottom: '24px' }} onClick={onAddKR} StartIcon={Plus}>
      Add
    </Button>
  ) : null;
};

const NoRowsOverlayComponent = ({ isMyOkr, isMyCompany, isCheckIn }) => {
  const mobileViewProps = isMobileView() ? { height: '50px', marginTop: '5px', marginBottom: '0px' } : {};
  const { moduleName } = useOrganisationSettings();
  const { canCreateKeyResultUnderKeyResult, canCreateKeyResultUnderObjective, canCreateKeyResultAtRootLevel } =
    useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges();

  const canCreateKeyResult =
    canCreateKeyResultAtRootLevel || canCreateKeyResultUnderObjective || canCreateKeyResultUnderKeyResult;

  const moduleNameToUse = moduleName;

  if (isCheckIn) {
    return (
      <EmptyScreen
        heading={`Hey there! It looks like there are no ${modifyString(
          moduleName,
          StringModifier.PLURAL_SENTENCE_CASE
        )} to check into at the moment.`}
        // description={`Read about how you can set up ${moduleNameToUse}s for your company or speak to your CSM.`}
        illustrationSrc={GoalsNoDataImageV1}
        isSmallTextSize
        smallTextMargin="5px"
        illustrationStyle={{ marginTop: '64px', ...mobileViewProps }}
        CTAComp={null}
      />
    );
  }

  if (isMyCompany) {
    return (
      <EmptyScreen
        heading={`You can easily see the details, track the progress and nudge key results owners of all the ${moduleNameToUse}s across your company here.`}
        description={`Read about how you can set up ${moduleNameToUse}s for your company or speak to your CSM.`}
        illustrationSrc={GoalsNoDataImageV1}
        isSmallTextSize
        smallTextMargin="5px"
        illustrationStyle={{ marginTop: '64px', ...mobileViewProps }}
        CTAComp={isMobileView() ? null : CTA}
      />
    );
  }

  if (!isMyOkr) {
    return (
      <EmptyScreen
        heading={`Once your "Team/Organization/Cohort" has ${moduleNameToUse}s, you can easily check the status in a list view here or a card/tree view in subsequent tabs.`}
        description={canCreateKeyResult ? `Let's start with adding a new ${moduleNameToUse} for them.` : ''}
        illustrationSrc={GoalsNoDataImageV1}
        illustrationStyle={{ marginTop: '64px', ...mobileViewProps }}
        CTAComp={isMobileView() ? null : CTA}
        isSmallTextSize
        smallTextMargin="5px"
      />
    );
  }

  return (
    <EmptyScreen
      heading={`${moduleNameToUse}s are critical for helping both you and your company achieve their next milestone.`}
      description={canCreateKeyResult ? `Start your journey by creating your first  ${moduleNameToUse} today!` : ''}
      illustrationSrc={GoalsNoDataImage2}
      illustrationStyle={{ marginTop: '64px', ...mobileViewProps }}
      CTAComp={isMobileView() ? null : CTA}
      isSmallTextSize
      smallTextMargin="5px"
    />
  );
};

export { NoRowsOverlayComponent };
